import { EmployeeRole } from '@/types';
import { PaymentMethodType } from '@/enums';
import { Platform } from 'react-native';
import {
  useDynamicConfigFlag,
  useDynamicConfigValue,
} from '@/hooks/use-dynamic-config-value';
import { useMemo } from 'react';

export interface PayCapability {
  canMakePayment: boolean;
  canPayByCheck: boolean;
  canRecordCashPayment: boolean;
  canRecordCheckPayment: boolean;
  canShareLink: boolean;
  canShortPay: boolean;
  isDistributorUser: boolean;
  walletMethods: Set<PaymentMethodType>;
}

/**
 * Returns what payment-based actions are available based on the current user's
 * permissions and feature availability.
 *
 * @example
 * const { enabledPaymentMethods } = usePaymentsContext();
 * const {
 *   selectedRestaurantRelationship: {
 *     employeeRole: restaurantEmployeeRole,
 *     showPaymentLinks,
 *   },
 *   supplierEmployeeRole,
 * } = useAuthenticatedUserContext();
 * const capability = usePayCapability({
 *   enabledPaymentMethods,
 *   restaurantEmployeeRole,
 *   showPaymentLinks,
 *   supplierEmployeeRole,
 * });
 */
export const usePayCapability = ({
  enabledPaymentMethods,
  restaurantEmployeeRole,
  showPaymentLinks,
  supplierEmployeeRole,
}: {
  enabledPaymentMethods: PaymentMethodType[];
  restaurantEmployeeRole: EmployeeRole | null;
  showPaymentLinks: boolean;
  supplierEmployeeRole: EmployeeRole | null;
}): PayCapability => {
  const shortPayConfig = useDynamicConfigValue('short_pay_config');
  const shortPayEnabled = Boolean(shortPayConfig?.enabled);
  const checkCaptureEnabled = useDynamicConfigFlag(
    'jack_henry_checks_config',
    'check_capture_enabled',
  );
  const checkEnabled = checkCaptureEnabled && Platform.OS !== 'web';
  const cashPaymentsEnabled = Boolean(
    useDynamicConfigValue('cash_payments_enabled'),
  );
  const manualCheckPaymentsEnabled = Boolean(
    useDynamicConfigValue('manual_check_payments_enabled'),
  );

  return useMemo(() => {
    const isRestaurantUser = !!restaurantEmployeeRole;
    const isDistributorUser = !!supplierEmployeeRole && !restaurantEmployeeRole;

    const walletMethods = new Set<PaymentMethodType>();
    let canPayByCheck = false;
    let canRecordCashPayment = false;
    let canRecordCheckPayment = false;
    let canShareLink = false;
    let canMakePayment = false;

    if (isRestaurantUser) {
      enabledPaymentMethods.forEach((m) => walletMethods.add(m));
      canPayByCheck = checkEnabled;
      canMakePayment = true;
    } else if (isDistributorUser) {
      canShareLink = showPaymentLinks;
      canPayByCheck = checkEnabled;
      canRecordCashPayment = cashPaymentsEnabled;
      canRecordCheckPayment = manualCheckPaymentsEnabled;
      canMakePayment =
        canPayByCheck || canRecordCashPayment || canRecordCheckPayment;
    }

    const canShortPay = shortPayEnabled && canMakePayment;

    return {
      canMakePayment,
      canPayByCheck,
      canRecordCashPayment,
      canRecordCheckPayment,
      canShareLink,
      canShortPay,
      isDistributorUser,
      walletMethods,
    };
  }, [
    cashPaymentsEnabled,
    checkEnabled,
    enabledPaymentMethods,
    manualCheckPaymentsEnabled,
    restaurantEmployeeRole,
    shortPayEnabled,
    showPaymentLinks,
    supplierEmployeeRole,
  ]);
};
